import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, Pencil, Archive, ArchiveRestore, Trash2,
  AlertTriangle, Check, X,
} from 'lucide-react';
import { money, type DeviceBundleRow, type DeviceModelRow } from '../../services/hardwareApi';
import { billingApi, type BillingSkuRow, type FinanceAgreementRow } from '../../services/billingApi';
import { RUBY, RUBY_DEEP } from './InventoryChrome';

/**
 * POLICIES — the OFFER side of the Financing page.
 *
 * The wizard creates billing SKUs (`type: 'device_finance'`), but the
 * agreements table below only shows LIVE Stripe subscriptions — so a freshly
 * created policy used to "disappear" until the first customer financed
 * something. This section is the missing catalog view: every device offer,
 * offered or retired, with its sales rolled up underneath.
 *
 * Lifecycle actions per offer:
 *  - Edit      — partial `upsertSku` (fields left out survive untouched). If
 *                the price amount changes on an offer that already has a
 *                Stripe price, the stale price pointers are cleared and a new
 *                price is minted immediately — otherwise new checkouts would
 *                silently keep billing the OLD amount forever (`ensurePrice`
 *                reuses an existing pointer regardless of amount).
 *  - Retire    — `status: 'deprecated'`. Every offer surface filters
 *                `status === 'active'`, so a retired offer stops being
 *                offered instantly; existing agreements keep billing (their
 *                Stripe subscriptions carry their own price id).
 *  - Re-offer  — `status: 'active'` again.
 *  - Delete    — irreversible; server refuses non-retired offers, and the UI
 *                refuses while ANY agreement (live or past) references the
 *                SKU, because the financing book joins name/term through it.
 *
 * "When it will be over": per line, a termed agreement ends `startedAt +
 * termMonths`; a cancelling rental ends at `currentPeriodEndAt`; an
 * open-ended rental is honestly "ongoing" — it has no end until cancelled.
 */

interface Props {
  skus: BillingSkuRow[];              // device_finance only
  /**
   * The WHOLE book, every status, paged to exhaustion by the host screen —
   * not one page of it. Delete-refusal below (`deleteBlocked`) depends on
   * this being complete: a SKU with agreements sitting past the first page,
   * or whose only agreements are closed/cancelled, must never look empty
   * just because the fetch stopped early.
   */
  agreements: FinanceAgreementRow[];
  /**
   * Whether the `agreements` fetch above completed AND was not cut off by
   * its own page cap. Defaults to `false` (unknown) so a slow first render,
   * a failed fetch, or a truncated one all make the delete gate REFUSE
   * rather than trust an incomplete list — a safety check that silently
   * weakens when its data is truncated is worse than no check at all.
   */
  agreementsComplete?: boolean;
  models: DeviceModelRow[];
  bundles: DeviceBundleRow[];
  onChanged: () => Promise<void> | void;
  /**
   * `page` (default) is the standalone section: its own heading, the sale-mode
   * rollup chips, and bottom margin.
   *
   * `inline` is the same cards rendered INSIDE one device's expanded row on the
   * merged Equipment & Financing screen (2026-08-30). The heading and rollups
   * are dropped there because the row already says which device this is, and a
   * rollup over one device's offers only repeats the cards below it. Every
   * lifecycle action is identical in both — this changes chrome, never
   * behaviour.
   */
  variant?: 'page' | 'inline';
  /** What the empty state should tell an operator to do; inline rows have their own button. */
  emptyHint?: string;
}

const MODE_BADGE: Record<string, { label: string; cls: string }> = {
  outright: { label: 'BUY', cls: 'bg-blue-100 text-blue-700' },
  installment: { label: 'FINANCE', cls: 'bg-rose-100 text-rose-700' },
  lease: { label: 'LEASE', cls: 'bg-purple-100 text-purple-700' },
  rental: { label: 'RENT', cls: 'bg-teal-100 text-teal-700' },
  included: { label: 'INCLUDED', cls: 'bg-stone-200 text-stone-600' },
};

const badgeFor = (sku: BillingSkuRow) => MODE_BADGE[sku.saleMode || ''] || { label: sku.saleMode || '?', cls: 'bg-stone-200 text-stone-600' };

const addMonths = (epochMs: number, months: number): Date => {
  const d = new Date(epochMs);
  d.setMonth(d.getMonth() + months);
  return d;
};

/** When one agreement line ends — a date, 'ongoing', or unknown ('—'). */
const lineEndLabel = (r: FinanceAgreementRow): string => {
  if (r.subscriptionStatus === 'canceled') return 'ended';
  if (r.cancelAtPeriodEnd) {
    return r.currentPeriodEndAt ? `ends ${new Date(r.currentPeriodEndAt).toLocaleDateString()}` : 'ending at period end';
  }
  if (r.financeType === 'rental' && (r.rentalKind ?? 'open_ended') === 'open_ended') return 'ongoing';
  if (r.termMonths && r.startedAt) return `ends ${addMonths(r.startedAt, r.termMonths).toLocaleDateString()}`;
  return '—';
};

interface PolicyStats {
  lines: FinanceAgreementRow[];   // every agreement ever on this SKU
  liveLines: FinanceAgreementRow[];
  monthlyCents: number;           // live run rate
  collectedCents: number;         // all-time, incl. ended lines
  projectedCents: number | null;  // sum of termed totals; null when any live line is open-ended
}


/** Short absolute timestamp — never "3 days ago": an audit line has to stay true when re-read. */
const stamp = (ms?: number): string =>
  typeof ms === 'number' && ms > 0
    ? new Date(ms).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

const HISTORY_VERB: Record<string, { label: string; dot: string }> = {
  created: { label: 'Policy created', dot: 'bg-emerald-500' },
  updated: { label: 'Terms edited', dot: 'bg-red-500' },
  retired: { label: 'Retired', dot: 'bg-amber-500' },
  reoffered: { label: 'Offered again', dot: 'bg-emerald-500' },
};

/**
 * The SKU's own append-only change log (2026-08-27), newest first. Stamped
 * server-side by `catalog.upsertSku` from the admin's token, so an offer
 * created before that shipped simply has none — said plainly rather than
 * rendered as an empty timeline that reads like "nothing ever happened".
 */
const SkuHistory: React.FC<{ sku: BillingSkuRow }> = ({ sku }) => {
  const entries = [...(sku.history || [])].reverse();
  if (!entries.length) {
    return (
      <p className="text-[11px] text-stone-500">
        No change history recorded for this offer{sku.createdAt ? ` — it predates edit tracking` : ''}. Future edits will appear here.
      </p>
    );
  }
  return (
    <ol className="relative ml-1 border-l border-stone-200 pl-3.5">
      {entries.map((e, i) => {
        const v = HISTORY_VERB[e.action] || { label: e.action, dot: 'bg-stone-400' };
        return (
          <li key={`${e.at}-${i}`} className="relative pb-3 last:pb-0">
            <span className={`absolute -left-[19px] top-1 h-2 w-2 rounded-full ring-2 ring-white ${v.dot}`} />
            <p className="text-[12px] font-semibold text-stone-800">{v.label}</p>
            <p className="text-[10.5px] text-stone-400">{e.by} · {stamp(e.at)}</p>
            {e.changes?.length ? (
              <ul className="mt-1 space-y-0.5">
                {e.changes.map((c, j) => (
                  <li key={j} className="break-words text-[11px] text-stone-600">
                    {c.field}
                    {c.from !== undefined && <> <span className="text-stone-400 line-through">{c.from}</span></>}
                    {c.to !== undefined && <> → <span className="font-medium text-stone-800">{c.to}</span></>}
                    {c.to === undefined && c.from !== undefined && <> → <span className="italic text-stone-500">cleared</span></>}
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
};

const FinancingPoliciesSection: React.FC<Props> = ({
  skus, agreements, agreementsComplete = false, models, bundles, onChanged, variant = 'page', emptyHint,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  // Retire / re-offer / delete confirm modal (custom — never a native confirm)
  const [confirm, setConfirm] = useState<{ sku: BillingSkuRow; action: 'retire' | 'reoffer' | 'delete' } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const modelById = useMemo(() => new Map(models.map((m) => [m.modelId, m])), [models]);
  const bundleById = useMemo(() => new Map(bundles.map((b) => [b.bundleId, b])), [bundles]);

  const statsBySku = useMemo(() => {
    const map = new Map<string, PolicyStats>();
    for (const sku of skus) {
      const lines = agreements.filter((r) => r.skuId === sku.skuId);
      const liveLines = lines.filter((r) => r.subscriptionStatus !== 'canceled');
      const openEnded = liveLines.some((r) => r.financeType === 'rental' && (r.rentalKind ?? 'open_ended') === 'open_ended' && !r.cancelAtPeriodEnd);
      map.set(sku.skuId, {
        lines,
        liveLines,
        monthlyCents: liveLines.reduce((s, r) => s + r.monthlyCents, 0),
        collectedCents: lines.reduce((s, r) => s + r.paidCents, 0),
        projectedCents: openEnded ? null : liveLines.reduce((s, r) => s + (r.totalCents ?? 0), 0) || null,
      });
    }
    return map;
  }, [skus, agreements]);

  // "Sales by policy type" — one rollup chip per sale mode that has offers.
  const typeRollups = useMemo(() => {
    const acc: Record<string, { offers: number; lines: number; monthlyCents: number; collectedCents: number }> = {};
    for (const sku of skus) {
      const key = sku.saleMode || 'unknown';
      const st = statsBySku.get(sku.skuId);
      acc[key] = acc[key] || { offers: 0, lines: 0, monthlyCents: 0, collectedCents: 0 };
      acc[key].offers += 1;
      acc[key].lines += st?.liveLines.length || 0;
      acc[key].monthlyCents += st?.monthlyCents || 0;
      acc[key].collectedCents += st?.collectedCents || 0;
    }
    return acc;
  }, [skus, statsBySku]);

  const deviceNameFor = (sku: BillingSkuRow): string =>
    (sku.bundleId ? bundleById.get(sku.bundleId)?.name : sku.modelId ? modelById.get(sku.modelId)?.modelName : undefined)
    || sku.bundleId || sku.modelId || '—';

  const termsSummary = (sku: BillingSkuRow): string => {
    if (sku.saleMode === 'outright') return `${money(sku.amountCents)} once`;
    if (sku.saleMode === 'rental') {
      const kind = sku.rentalKind === 'fixed_term' ? `${sku.termMonths} mo fixed` : 'open-ended';
      const deposit = (sku.depositCents || 0) > 0 ? ` · ${money(sku.depositCents!)} deposit` : '';
      const min = (sku.minCommitmentMonths || 0) > 0 ? ` · min ${sku.minCommitmentMonths} mo` : '';
      return `${money(sku.amountCents)}/mo · ${kind}${min}${deposit}`;
    }
    const down = (sku.downPaymentCents || 0) > 0 ? ` · ${money(sku.downPaymentCents!)} down` : '';
    return `${money(sku.amountCents)}/mo × ${sku.termMonths ?? '—'} mo${down}`;
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      if (confirm.action === 'delete') {
        await billingApi.deleteSku(confirm.sku.skuId);
        setNotice({ ok: true, text: `${confirm.sku.skuId} deleted.` });
      } else {
        const status = confirm.action === 'retire' ? 'deprecated' : 'active';
        await billingApi.upsertSku({ skuId: confirm.sku.skuId, status });
        setNotice({
          ok: true,
          text: confirm.action === 'retire'
            ? `${confirm.sku.skuId} retired — it no longer shows as an option on new orders. Existing agreements keep billing to their end.`
            : `${confirm.sku.skuId} is offered again.`,
        });
      }
      setConfirm(null);
      await onChanged();
    } catch (e: any) {
      setConfirmError(e?.message || 'Action failed');
    } finally {
      setConfirmBusy(false);
    }
  };

  const sorted = [...skus].sort((a, b) => {
    // Offered first, then by device name, then displayOrder.
    if ((a.status === 'active') !== (b.status === 'active')) return a.status === 'active' ? -1 : 1;
    return deviceNameFor(a).localeCompare(deviceNameFor(b)) || a.displayOrder - b.displayOrder || a.skuId.localeCompare(b.skuId);
  });

  const inline = variant === 'inline';

  return (
    <div className={inline ? '' : 'mb-8'}>
      {!inline && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-bold text-stone-900">Policies (offers)</h2>
            <p className="text-[11px] text-stone-500">
              Every device offer the wizard has created — offered or retired — with its sales rolled up. Retiring an
              offer removes it from new orders only; agreements already running keep billing.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(typeRollups).map(([mode, r]) => {
              const b = MODE_BADGE[mode] || { label: mode, cls: 'bg-stone-200 text-stone-600' };
              return (
                <span key={mode} className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-2 py-1 text-[10px] text-stone-600">
                  <span className={`rounded px-1 py-0.5 text-[8.5px] font-bold ${b.cls}`}>{b.label}</span>
                  {r.lines} line{r.lines === 1 ? '' : 's'} · {money(r.monthlyCents)}/mo · {money(r.collectedCents)} collected
                </span>
              );
            })}
          </div>
        </div>
      )}

      {notice && (
        <div className={`mb-3 flex items-start justify-between gap-2 rounded-xl border p-3 text-xs ${
          notice.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-red-50 text-red-800'
        }`}>
          <span className="flex items-start gap-2">
            {notice.ok ? <Check size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
            {notice.text}
          </span>
          <button onClick={() => setNotice(null)} className="shrink-0 opacity-60 hover:opacity-100"><X size={14} /></button>
        </div>
      )}

      {sorted.length === 0 ? (
        <div className={`rounded-xl border border-dashed border-stone-300 bg-white text-center text-xs text-stone-500 ${inline ? 'py-5' : 'py-10'}`}>
          {emptyHint || 'No financing policies yet — create one with the button above.'}
        </div>
      ) : (
        /* CARDS, NOT A TABLE (2026-08-27). The old 10-column table lived in an
           `overflow-x-auto` wrapper with `whitespace-nowrap` cells, so reading
           a policy meant scrolling sideways — and the nested 7-column
           subscriptions table made it worse. Everything below wraps: stats
           flex-wrap, long SKU ids break, and the expanded detail is two
           stacked columns that collapse to one on a narrow screen. */
        <div className="flex flex-col gap-2.5">
          {sorted.map((sku) => {
            const st = statsBySku.get(sku.skuId);
            const b = badgeFor(sku);
            const isOpen = !!expanded[sku.skuId];
            const offered = sku.status === 'active';
            const hasLines = (st?.lines.length || 0) > 0;
            // Delete-refusal must never infer "no agreements" from a book we
            // know is incomplete — absent/false `agreementsComplete` blocks
            // deletion regardless of what this (possibly truncated) `lines`
            // count says.
            const deleteBlocked = !agreementsComplete || hasLines;
            const editTarget = sku.bundleId ? `bundle:${sku.bundleId}` : sku.modelId ? `model:${sku.modelId}` : '';
            return (
              <div key={sku.skuId} className={`rounded-xl border border-stone-200 ${offered ? 'bg-white' : 'bg-stone-50/70'}`}>
                {/* header */}
                <div className="flex flex-wrap items-start gap-3 p-3.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-bold text-stone-900">{deviceNameFor(sku)}</span>
                      <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${b.cls}`}>{b.label}</span>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                        offered ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'
                      }`}>
                        {offered ? 'offered' : 'retired'}
                      </span>
                      {!sku.hasPrice && offered && (
                        <span
                          className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700"
                          title="No Stripe price exists for this stage — checkout fails until one is generated."
                        >
                          no price
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-[11.5px] text-stone-500">{sku.name}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      onClick={() => (editTarget
                        ? navigate(`/inventory/financing/edit?edit=${editTarget}`)
                        : setNotice({ ok: false, text: 'This offer has no device attached, so it cannot be opened in the wizard. Fix its model/bundle link first.' }))}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50"
                      title="Edit this device's whole policy in the wizard that created it"
                    >
                      <Pencil size={12} /> Edit in wizard
                    </button>
                    {offered ? (
                      <button onClick={() => { setConfirm({ sku, action: 'retire' }); setConfirmError(null); }} title="Retire — stop offering (existing agreements keep billing)"
                        className="rounded-lg border border-stone-200 p-1.5 text-stone-500 hover:border-amber-300 hover:text-amber-600">
                        <Archive size={13} />
                      </button>
                    ) : (
                      <>
                        <button onClick={() => { setConfirm({ sku, action: 'reoffer' }); setConfirmError(null); }} title="Offer again"
                          className="rounded-lg border border-stone-200 p-1.5 text-stone-500 hover:border-emerald-300 hover:text-emerald-600">
                          <ArchiveRestore size={13} />
                        </button>
                        <button
                          onClick={() => { if (!deleteBlocked) { setConfirm({ sku, action: 'delete' }); setConfirmError(null); } }}
                          disabled={deleteBlocked}
                          title={
                            !agreementsComplete
                              ? 'Cannot confirm this offer has no agreements (the agreements list failed to load). Retire it instead.'
                              : hasLines
                                ? 'Cannot delete — agreements reference this offer. Keep it retired instead.'
                                : 'Delete permanently'
                          }
                          className="rounded-lg border border-stone-200 p-1.5 text-stone-500 enabled:hover:border-red-300 enabled:hover:text-red-600 disabled:opacity-30"
                        >
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* stat strip — wraps rather than scrolls */}
                <div className="flex flex-wrap border-t border-stone-200/70 bg-stone-50/60">
                  {[
                    { k: 'Terms', v: termsSummary(sku) },
                    { k: 'Active lines', v: String(st?.liveLines.length || 0) },
                    { k: 'Monthly', v: money(st?.monthlyCents || 0) },
                    {
                      k: 'Collected',
                      v: `${money(st?.collectedCents || 0)}${st?.projectedCents ? ` / ${money(st.projectedCents)}` : ''}`,
                    },
                  ].map((s) => (
                    <div key={s.k} className="min-w-0 flex-1 basis-28 border-r border-stone-200/70 px-3.5 py-2 last:border-r-0">
                      <p className="text-[9.5px] font-semibold uppercase tracking-wider text-stone-400">{s.k}</p>
                      <p className="mt-0.5 break-words font-mono text-[12.5px] font-semibold text-stone-800">{s.v}</p>
                    </div>
                  ))}
                </div>

                {/* provenance — the SKU, who made it, who last touched it */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-stone-200/70 px-3.5 py-2 text-[11px] text-stone-500">
                  <span className="break-all rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[10.5px] text-stone-700">{sku.skuId}</span>
                  <span>
                    Created{sku.createdBy ? <> by <span className="font-semibold text-stone-700">{sku.createdBy}</span></> : null}
                    {sku.createdAt ? ` · ${stamp(sku.createdAt)}` : ''}
                    {!sku.createdBy && !sku.createdAt ? ' — not recorded' : ''}
                  </span>
                  <span>
                    {sku.updatedBy
                      ? <>Last edited by <span className="font-semibold text-stone-700">{sku.updatedBy}</span>{sku.updatedAt ? ` · ${stamp(sku.updatedAt)}` : ''}</>
                      : 'Never edited'}
                  </span>
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [sku.skuId]: !p[sku.skuId] }))}
                    className="ml-auto inline-flex items-center gap-1 rounded-lg border border-stone-200 bg-white px-2 py-1 text-[10.5px] font-semibold text-stone-600 hover:bg-stone-50"
                  >
                    {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    {isOpen ? 'Hide' : 'Subscriptions & history'}
                  </button>
                </div>

                {/* expanded: subscriptions + history, side by side, both stacked */}
                {isOpen && (
                  <div className="grid grid-cols-1 gap-0 border-t border-stone-200/70 lg:grid-cols-2">
                    <div className="min-w-0 p-3.5">
                      <p className="mb-2 text-[9.5px] font-semibold uppercase tracking-wider text-stone-400">
                        Subscriptions{hasLines ? ` · ${st!.lines.length}` : ''}
                      </p>
                      {!hasLines ? (
                        <p className="text-[11px] text-stone-500">
                          {agreementsComplete
                            ? 'No agreements under this policy yet.'
                            : 'Agreements for this policy could not be confirmed.'}
                        </p>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          {st!.lines.map((r, i) => {
                            const pct = r.termMonths ? Math.min(100, Math.round((r.monthsPaid / r.termMonths) * 100)) : null;
                            return (
                              <div
                                key={`${r.subscriptionId}-${i}`}
                                className={`rounded-lg border border-stone-200 bg-white p-2.5 ${r.subscriptionStatus === 'canceled' ? 'opacity-60' : ''}`}
                              >
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                  <span className="text-[12px] font-semibold text-stone-800">
                                    {r.tenantName || r.tenantId || '— unattributed —'}{r.quantity > 1 ? ` ×${r.quantity}` : ''}
                                  </span>
                                  <span className="font-mono text-[12px] font-semibold text-stone-800">{money(r.monthlyCents)}</span>
                                </div>
                                {pct !== null && (
                                  <div className="mt-1.5 h-1 overflow-hidden rounded bg-stone-200">
                                    <div className="h-full rounded bg-emerald-500" style={{ width: `${pct}%` }} />
                                  </div>
                                )}
                                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10.5px] text-stone-500">
                                  <span className="font-mono">{r.monthsPaid}{r.termMonths ? `/${r.termMonths}` : ''} mo</span>
                                  <span className="font-mono">{money(r.paidCents)} collected</span>
                                  <span className="font-mono">{r.remainingCents === null ? 'open-ended' : `${money(r.remainingCents)} left`}</span>
                                  <span>{r.startedAt ? `from ${new Date(r.startedAt).toLocaleDateString()}` : 'start unknown'}</span>
                                  <span>{lineEndLabel(r)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 border-t border-stone-200/70 p-3.5 lg:border-l lg:border-t-0">
                      <p className="mb-2 text-[9.5px] font-semibold uppercase tracking-wider text-stone-400">Edit history</p>
                      <SkuHistory sku={sku} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* One-time BUY sales never appear in the Stripe-derived agreement book
          (they are one-off invoices, not subscriptions) — say so rather than
          showing zeros that read as "no sales". */}
      {skus.some((s) => s.saleMode === 'outright') && (
        <p className="mt-2 text-[10.5px] text-stone-400">
          BUY (outright) offers bill as one-time invoices, so their sales are not part of the subscription-derived
          lines/collected figures here.
        </p>
      )}

      {/* ---- Retire / re-offer / delete confirm ---- */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !confirmBusy && setConfirm(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-bold text-stone-900">
                {confirm.action === 'retire' ? 'Retire this offer?' : confirm.action === 'reoffer' ? 'Offer this again?' : 'Delete this offer permanently?'}
              </h3>
              <button onClick={() => !confirmBusy && setConfirm(null)} className="text-stone-400 hover:text-stone-600"><X size={16} /></button>
            </div>
            <p className="text-[11.5px] text-stone-600 leading-relaxed">
              <span className="font-semibold text-stone-800">{confirm.sku.name}</span>
              <span className="font-mono text-stone-400"> · {confirm.sku.skuId}</span>
            </p>
            <p className="mt-2 text-[11.5px] text-stone-600 leading-relaxed">
              {confirm.action === 'retire' && (
                <>It stops appearing as an option on new orders immediately. Agreements already running are untouched
                — they keep billing to their own end. You can offer it again any time.</>
              )}
              {confirm.action === 'reoffer' && (
                <>It becomes selectable on new orders again{!confirm.sku.hasPrice ? ' — but it has NO Stripe price for this stage, so generate one before it is sold' : ''}.</>
              )}
              {confirm.action === 'delete' && (
                <>This cannot be undone. No agreement references this offer, so nothing loses its history — but the
                offer, its terms and its price pointers are gone for good.</>
              )}
            </p>
            {confirmError && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-[11px] text-red-800">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {confirmError}
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setConfirm(null)} disabled={confirmBusy}
                className="rounded-lg border border-stone-300 px-3.5 py-2 text-xs font-semibold text-stone-600">
                Cancel
              </button>
              <button onClick={runConfirm} disabled={confirmBusy}
                className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-bold text-white disabled:opacity-50 ${
                  confirm.action === 'delete' ? 'bg-red-600' : ''
                }`}
                style={confirm.action === 'delete' ? undefined : { background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})` }}>
                {confirmBusy ? <RubyGemMark size={13} drawing color="currentColor" /> : null}
                {confirm.action === 'retire' ? 'Retire offer' : confirm.action === 'reoffer' ? 'Offer again' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancingPoliciesSection;
